// Small form controls shared by the Sticker Studio panels (dark, compact — matches the workspace sidebar).
import { useEffect, useState } from "react";
import { SWATCHES } from "./stickerModel";

export const FIELD = "h-7 rounded-md border border-[#27324a] bg-[#0c1017] px-2 text-[11px] text-[#e6e8ec] outline-none focus:border-[#4c9ffe]";
export const Row = ({ label, children, hint }) => (
  <div className="flex items-center gap-2" title={hint}>
    <span className="w-[72px] flex-none text-[10px] uppercase tracking-wide text-[#6b7079]">{label}</span>
    <div className="flex min-w-0 flex-1 items-center gap-1.5">{children}</div>
  </div>
);
export const Section = ({ title, children, right }) => (
  <div className="flex flex-col gap-2 border-b border-[#1f2733] px-3 py-3">
    <div className="flex items-center justify-between"><span className="text-[11px] font-semibold text-[#e6e8ec]">{title}</span>{right}</div>
    {children}
  </div>
);
// Number field that lets you clear and retype: the value is applied (clamped) on blur / Enter, not on every keystroke.
export const Num = ({ value, onChange, step = 0.125, min = 0, max = 999, unit = "″", id, className = "" }) => {
  const shown = String(Math.round(value * 1000) / 1000);
  const [txt, setTxt] = useState(shown);
  const [focus, setFocus] = useState(false);
  useEffect(() => { if (!focus) setTxt(shown); }, [shown, focus]);
  const apply = () => { const v = Number(txt); if (txt.trim() !== "" && !Number.isNaN(v)) onChange(Math.max(min, Math.min(max, v))); else setTxt(shown); };
  return (
    <label className={`relative flex items-center ${className}`}>
      <input data-testid={id} type="number" value={txt} step={step} min={min} max={max} onFocus={() => setFocus(true)} onChange={(e) => setTxt(e.target.value)}
        onBlur={() => { setFocus(false); apply(); }} onKeyDown={(e) => { if (e.key === "Enter") { apply(); e.currentTarget.blur(); } }} className={`${FIELD} w-full pr-5`} />
      <span className="pointer-events-none absolute right-1.5 text-[10px] text-[#6b7079]">{unit}</span>
    </label>
  );
};
export const Sel = ({ value, onChange, options, id, className = "" }) => (
  <select data-testid={id} value={value} onChange={(e) => onChange(e.target.value)} className={`${FIELD} w-full ${className}`}>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);
export const Color = ({ value, onChange, id, allowNone = false }) => (
  <div className="flex flex-wrap items-center gap-1">
    {allowNone && <button type="button" onClick={() => onChange(null)} title="None" className={`h-5 w-5 rounded border ${value == null ? "border-[#4c9ffe]" : "border-[#27324a]"} bg-[linear-gradient(135deg,transparent_45%,#e5484d_45%,#e5484d_55%,transparent_55%)]`} data-testid={`${id}-none`} />}
    {SWATCHES.map((c) => <button key={c} type="button" onClick={() => onChange(c)} title={c} style={{ background: c }} className={`h-5 w-5 rounded border ${value === c ? "border-[#4c9ffe] ring-1 ring-[#4c9ffe]" : "border-[#27324a]"}`} />)}
    <label className="relative h-5 w-5 cursor-pointer overflow-hidden rounded border border-[#27324a] bg-[conic-gradient(red,yellow,lime,cyan,blue,magenta,red)]" title="Custom color">
      <input data-testid={id} type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} className="absolute -left-2 -top-2 h-10 w-10 cursor-pointer opacity-0" />
    </label>
  </div>
);
export const Btn = ({ children, onClick, id, tone = "", disabled, title, className = "" }) => (
  <button type="button" data-testid={id} onClick={onClick} disabled={disabled} title={title} className={`flex h-7 items-center justify-center gap-1.5 rounded-md border px-2.5 text-[11px] font-medium transition-colors disabled:opacity-50 ${tone === "primary" ? "border-[#2f80ff] bg-[#2f80ff] text-white hover:bg-[#4c9ffe]" : tone === "amber" ? "border-[#f5a524]/60 bg-[#f5a524]/15 text-[#ffd27a] hover:bg-[#f5a524]/25" : "border-[#27324a] bg-[#0c1017] text-[#c7cbd1] hover:border-[#3b4a6b] hover:text-white"} ${className}`}>{children}</button>
);
